import { Capacitor } from '@capacitor/core';
import { useCallback, useState } from 'react';
import { currentRoute, navStore, resetNav } from '../../app/navigation';
import { webVersionUi } from '../../app/webVersion';
import { useT, type MessageKey } from '../../i18n/i18n';
import { signInWithApple } from '../../native/appleSignIn';
import { signInWithGoogle } from '../../native/googleSignIn';
import { useStore } from '../../state/createStore';
import { takeSignOutReason } from '../../state/accountStore';
import {
  completePasswordReset,
  completeSessionRefresh,
  createAccount,
  resendVerificationEmail,
  sendPasswordReset,
  sendSignInCode,
  sessionStore,
  signInWithPassword,
  verifyEmailCode,
  verifySignInCode,
  type AuthCode,
  type AuthOutcome
} from '../../state/sessionStore';
import { useBackHandler } from '../../ui/backStack';
import { Banner, Button, Card, Field, InlineMessage, TextInput } from '../../ui/components';
import { useOnlineStatus } from '../../ui/hooks';

// Sign in (Owner task book 2026-09-04 §4): signing in is REQUIRED before inspecting — no guest
// mode, no local-first, no "later". Apple · Google · Email + password · Create Account · Forgot /
// Reset Password · Email verification. The hero copy is the Owner's product copy, verbatim.
// A remembered valid session continues offline (src/state/sessionStore.ts) — that never passes
// through this screen.
// 'code' = sign in with a code sent to the e-mail (the web version's way in for Apple / Google accounts).
type Mode = 'signIn' | 'create' | 'verify' | 'forgot' | 'reset' | 'code';

const MESSAGE_KEYS: Record<AuthCode, MessageKey> = {
  ok: 'auth.msg.ok',
  checkEmail: 'auth.msg.checkEmail',
  resetSent: 'auth.msg.resetSent',
  codeSent: 'auth.msg.codeSent',
  tooSoon: 'auth.msg.tooSoon',
  notConfigured: 'auth.msg.notConfigured',
  invalidCredentials: 'auth.msg.invalidCredentials',
  emailNotConfirmed: 'auth.msg.emailNotConfirmed',
  emailTaken: 'auth.msg.emailTaken',
  weakPassword: 'auth.msg.weakPassword',
  invalidCode: 'auth.msg.invalidCode',
  network: 'auth.msg.network',
  cancelled: 'auth.msg.cancelled',
  notNative: 'auth.msg.notNative',
  appleUnavailable: 'auth.msg.appleUnavailable',
  googleUnavailable: 'auth.msg.googleUnavailable',
  unknown: 'auth.msg.unknown'
};

const MIN_PASSWORD = 8;

export function SignInScreen() {
  const { t } = useT();
  const backendConfigured = useStore(sessionStore, (s) => s.backendConfigured);
  // Why the inspector is here (account just deleted); shown until they move on from this screen.
  const notice = useStore(navStore, (s) => {
    const route = currentRoute(s);
    return route.screen === 'signIn' ? (route.notice ?? null) : null;
  });
  const online = useOnlineStatus();
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  // Email-code sign-in: whether the code has gone out (the code field appears then).
  const [codeSent, setCodeSent] = useState(false);
  // Owner decision list 2026-10-19 item 50 (L-5): said once, when a newer device removed this one.
  const [revoked] = useState(() => takeSignOutReason() === 'deviceRevoked');

  function show(outcome: AuthOutcome) {
    const base = t(MESSAGE_KEYS[outcome.code]);
    setMessage({ ok: outcome.ok, text: outcome.code === 'unknown' && outcome.detail ? `${base} (${outcome.detail})` : base });
  }

  // Signing in now also opens the account's own data (account isolation 2026-09-21). If that fails — a
  // database the device refuses to open — the inspector is told, never left on a silent screen.
  async function finishSignedIn() {
    try {
      await completeSessionRefresh();
    } catch (raw) {
      show({ ok: false, code: 'unknown', detail: raw instanceof Error ? raw.message : String(raw) });
      return;
    }
    if (sessionStore.get().phase === 'signedIn') resetNav({ screen: 'home' });
  }

  async function run(work: () => Promise<AuthOutcome>, onOk?: (outcome: AuthOutcome) => Promise<void> | void) {
    setBusy(true);
    setMessage(null);
    try {
      const outcome = await work();
      if (outcome.code === 'cancelled') return;
      show(outcome);
      if (outcome.ok) await onOk?.(outcome);
    } finally {
      setBusy(false);
    }
  }

  const emailOk = /\S+@\S+\.\S+/.test(email.trim());
  const canSignIn = !busy && backendConfigured && emailOk && password.length > 0;
  const passwordValid = password.length >= MIN_PASSWORD;
  const canCreate = !busy && backendConfigured && emailOk && passwordValid && confirm === password;
  const switchMode = (next: Mode) => {
    setMode(next);
    setMessage(null);
    setCode('');
    setCodeSent(false);
  };

  function goTo(next: Mode) {
    return () => switchMode(next);
  }

  // Android system Back (emulator check 2026-10-20: on Create Account / Forgot password it sent the app to
  // the background): every other mode shows a 「返回登录」 button, so Back does the same; only the sign-in
  // form itself leaves it to the shell (minimize). While a request runs the on-screen button is disabled,
  // so Back is swallowed rather than minimizing mid-request.
  const backToSignIn = useCallback(() => {
    if (!busy) switchMode('signIn');
  }, [busy]);
  useBackHandler(backToSignIn, mode !== 'signIn');

  return (
    <div className="screen screen-auth" data-testid="sign-in-screen" data-mode={mode}>
      <div className="auth-hero">
        <p className="auth-brand">{t('shell.productName')}</p>
        <h1 className="auth-title">{t('auth.heroTitle')}</h1>
        <p className="auth-subtitle">{t('auth.heroSubtitle')}</p>
        <p className="auth-tags" data-testid="auth-tags">
          {t('auth.heroTags')}
        </p>
      </div>

      {notice === 'accountDeleted' ? <Banner tone="success" icon="check_circle" title={t('settings.deleteDone')} testId="auth-account-deleted" /> : null}
      {revoked ? <Banner tone="warning" icon="info" title={t('device.revoked')} testId="auth-device-revoked" /> : null}
      {!backendConfigured ? <Banner tone="error" icon="wifi_off" title={t('auth.notConfigured')} testId="auth-not-configured" /> : null}
      {backendConfigured && !online ? <Banner tone="warning" icon="wifi_off" title={t('auth.offlineNoSession')} testId="auth-offline" /> : null}

      <Card className="auth-card" testId={`auth-card-${mode}`}>
        {mode === 'signIn' ? (
          <div className="form-stack">
            <Field label={t('auth.email')} htmlFor="auth-email">
              <TextInput id="auth-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@example.com" testId="auth-email" />
            </Field>
            <Field label={t('auth.password')} htmlFor="auth-password">
              <PasswordInput id="auth-password" value={password} onChange={setPassword} autoComplete="current-password" testId="auth-password" />
            </Field>
            {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            <Button onClick={() => void run(() => signInWithPassword(email, password), finishSignedIn)} disabled={!canSignIn} testId="auth-sign-in">
              {t('auth.signIn')}
            </Button>
            <Button variant="text" onClick={goTo('forgot')} disabled={busy} testId="auth-forgot">
              {t('auth.forgot')}
            </Button>
            <p className="auth-divider">
              <span>{t('auth.or')}</span>
            </p>
            {webVersion() ? (
              // The web version (2026-10-20): Apple / Google sign-in live in the apps; an account made with
              // them has no password, so the web offers a code sent to the account's e-mail instead.
              <Button variant="outline" icon="mail" onClick={goTo('code')} disabled={busy || !backendConfigured} testId="auth-go-code">
                {t('auth.codeSignIn')}
              </Button>
            ) : (
              <>
                {appleSignInOffered() ? (
                  <Button variant="outline" icon="account_circle" onClick={() => void run(() => signInWithApple(), finishSignedIn)} disabled={busy || !backendConfigured} testId="auth-apple">
                    {t('auth.apple')}
                  </Button>
                ) : null}
                <Button variant="outline" icon="login" onClick={() => void run(() => googleOutcome(), finishSignedIn)} disabled={busy || !backendConfigured} testId="auth-google">
                  {t('auth.google')}
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={goTo('create')} disabled={busy} testId="auth-go-create">
              {t('auth.createAccount')}
            </Button>
          </div>
        ) : null}

        {mode === 'create' ? (
          <div className="form-stack">
            <h2 className="section-title">{t('auth.createTitle')}</h2>
            <p className="card-note">{t('auth.createBody')}</p>
            <Field label={t('auth.email')} htmlFor="auth-email">
              <TextInput id="auth-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@example.com" testId="auth-email" />
            </Field>
            <Field label={t('auth.password')} htmlFor="auth-password" hint={t('auth.passwordRules', { n: MIN_PASSWORD })}>
              <PasswordInput id="auth-password" value={password} onChange={setPassword} autoComplete="new-password" testId="auth-password" />
            </Field>
            <Field label={t('auth.confirmPassword')} htmlFor="auth-confirm">
              <PasswordInput id="auth-confirm" value={confirm} onChange={setConfirm} autoComplete="new-password" testId="auth-confirm" />
            </Field>
            {confirm.length > 0 && confirm !== password ? <InlineMessage kind="err" text={t('auth.passwordMismatch')} /> : null}
            {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            <Button
              onClick={() =>
                void run(
                  () => createAccount(email, password),
                  async (outcome) => {
                    if (outcome.code === 'checkEmail') switchMode('verify');
                    else await finishSignedIn();
                  }
                )
              }
              disabled={!canCreate}
              testId="auth-create"
            >
              {t('auth.createAccount')}
            </Button>
            <Button variant="text" onClick={goTo('signIn')} disabled={busy} testId="auth-back">
              {t('auth.haveAccount')}
            </Button>
          </div>
        ) : null}

        {mode === 'verify' ? (
          <div className="form-stack">
            <h2 className="section-title">{t('auth.verifyTitle')}</h2>
            <p className="card-note">{t('auth.verifyBody', { email: email.trim() })}</p>
            <Field label={t('auth.code')} htmlFor="auth-code">
              <TextInput id="auth-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} placeholder="12345678" testId="auth-code" />
            </Field>
            {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            <Button onClick={() => void run(() => verifyEmailCode(email, code), finishSignedIn)} disabled={busy || code.trim().length === 0} testId="auth-verify">
              {t('auth.verify')}
            </Button>
            <Button variant="text" onClick={() => void run(() => resendVerificationEmail(email))} disabled={busy} testId="auth-resend">
              {t('auth.resend')}
            </Button>
            <Button variant="text" onClick={goTo('signIn')} disabled={busy} testId="auth-back">
              {t('auth.backToSignIn')}
            </Button>
          </div>
        ) : null}

        {mode === 'code' ? (
          <div className="form-stack">
            <h2 className="section-title">{t('auth.codeSignInTitle')}</h2>
            <p className="card-note">{t('auth.codeSignInBody')}</p>
            <Field label={t('auth.email')} htmlFor="auth-email">
              <TextInput id="auth-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@example.com" testId="auth-email" />
            </Field>
            {/* Every message sits under the field it is about (Owner rule 9): before the code is sent that is
                the e-mail, afterwards the code. */}
            {!codeSent && message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            {codeSent ? (
              <>
                <Field label={t('auth.code')} htmlFor="auth-code">
                  <TextInput id="auth-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} placeholder="12345678" testId="auth-code" />
                </Field>
                {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
                <Button onClick={() => void run(() => verifySignInCode(email, code), finishSignedIn)} disabled={busy || code.trim().length === 0} testId="auth-code-sign-in">
                  {t('auth.signIn')}
                </Button>
                <Button variant="text" onClick={() => void run(() => sendSignInCode(email))} disabled={busy || !emailOk} testId="auth-code-resend">
                  {t('auth.resend')}
                </Button>
              </>
            ) : (
              <Button onClick={() => void run(() => sendSignInCode(email), () => setCodeSent(true))} disabled={busy || !backendConfigured || !emailOk} testId="auth-send-code">
                {t('auth.sendCode')}
              </Button>
            )}
            <Button variant="text" onClick={goTo('signIn')} disabled={busy} testId="auth-back">
              {t('auth.backToSignIn')}
            </Button>
          </div>
        ) : null}

        {mode === 'forgot' ? (
          <div className="form-stack">
            <h2 className="section-title">{t('auth.forgotTitle')}</h2>
            <p className="card-note">{t('auth.forgotBody')}</p>
            <Field label={t('auth.email')} htmlFor="auth-email">
              <TextInput id="auth-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={setEmail} placeholder="you@example.com" testId="auth-email" />
            </Field>
            {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            <Button onClick={() => void run(() => sendPasswordReset(email), () => switchMode('reset'))} disabled={busy || !backendConfigured || !emailOk} testId="auth-send-reset">
              {t('auth.sendReset')}
            </Button>
            <Button variant="text" onClick={goTo('signIn')} disabled={busy} testId="auth-back">
              {t('auth.backToSignIn')}
            </Button>
          </div>
        ) : null}

        {mode === 'reset' ? (
          <div className="form-stack">
            <h2 className="section-title">{t('auth.resetTitle')}</h2>
            <p className="card-note">{t('auth.resetBody', { email: email.trim() })}</p>
            <Field label={t('auth.code')} htmlFor="auth-code">
              <TextInput id="auth-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} placeholder="12345678" testId="auth-code" />
            </Field>
            <Field label={t('auth.newPassword')} htmlFor="auth-password" hint={t('auth.passwordRules', { n: MIN_PASSWORD })}>
              <PasswordInput id="auth-password" value={password} onChange={setPassword} autoComplete="new-password" testId="auth-password" />
            </Field>
            {message ? <InlineMessage kind={message.ok ? 'ok' : 'err'} text={message.text} /> : null}
            <Button onClick={() => void run(() => completePasswordReset(email, code, password), finishSignedIn)} disabled={busy || code.trim().length === 0 || !passwordValid} testId="auth-set-password">
              {t('auth.setPassword')}
            </Button>
            <Button variant="text" onClick={goTo('signIn')} disabled={busy} testId="auth-back">
              {t('auth.backToSignIn')}
            </Button>
          </div>
        ) : null}
      </Card>
      <p className="hint auth-hint">{t('auth.requiredNote')}</p>
    </div>
  );
}

// Sign in with Apple is offered on iOS and on the web (where it explains itself as iOS-only). On ANDROID the
// button is hidden: the app has no Apple Service ID for Android, so the plugin can only answer
// "apple.android.clientId is null or empty" (user walkthrough 2026-10-16). Hiding is a better answer than failing.
// The web version (a browser, not the iPhone / Android app — src/app/webVersion.ts).
function webVersion(): boolean {
  return webVersionUi();
}

function appleSignInOffered(): boolean {
  return Capacitor.getPlatform() !== 'android';
}

async function googleOutcome(): Promise<AuthOutcome> {
  const outcome = await signInWithGoogle();
  if (outcome.cancelled) return { ok: false, code: 'cancelled', detail: '' };
  if (outcome.ok) return { ok: true, code: 'ok', detail: '' };
  if (outcome.code === 'googleUnavailable' || outcome.code === 'notNative') return { ok: false, code: outcome.code, detail: '' };
  return { ok: false, code: 'unknown', detail: outcome.message };
}

function PasswordInput(props: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; testId: string }) {
  const { t } = useT();
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-field">
      <input
        id={props.id}
        className="field-input"
        type={visible ? 'text' : 'password'}
        autoComplete={props.autoComplete}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        data-testid={props.testId}
      />
      <button type="button" className="password-toggle" onClick={() => setVisible((v) => !v)} aria-label={visible ? t('auth.hidePassword') : t('auth.showPassword')}>
        {visible ? t('auth.hide') : t('auth.show')}
      </button>
    </div>
  );
}
